import Game from 'Game';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import { isIOS } from "Utility/Platform";
import { getEpisodeIndex } from "Utility/LevelHelper";
import { DEG_TO_RAD, Point } from 'pixi.js';
import { ILevelDataFile } from "System/Level/Data/ILevelDataFile";
import { ResourceID, ResourceURL } from '../Resource/IResourceDriver';
import GameObject from 'GameObject/GameObject';
import Element from 'Prototype/UI/Element';
import { IScoreEntry } from './Drivers/IScoreEntry';
import {
    get3StarShare,
    getFieldUnlockShare,
    getCropUnlockShare,
    perCrop,
    threeStarPerEpisode,
    getMyPetMiniGameJackpotShare,
    getPetUnlockShare,
    getLuckyWheelShare,
    getHardLevelShare,
} from "Constants/AvatarsAndCrops";
import { TextAlignment } from 'Prototype/UI/TextAlignment';
import { ILuckyWheelShareData } from 'System/UI/WheelPopup/WheelPopup';
import { RewardType } from '../RewardHelper';
import { CropUnlockLayout } from 'Layouts/Shares/CropUnlockLayout';
import SpriteComponent from 'Component/Render/Sprite';
import TextComponent from 'Component/Render/Text';
import { fit } from 'Utility/BoundsHelper';

async function loadIfNeeded(key: ResourceID, path: ResourceURL) {
    Game.resource.add(key, path);
    await Game.resource.wait(key);
}

async function waitForTextureKey(key: ResourceID, fallback: ResourceID): Promise<ResourceID> {
    if (Game.resource.existsAndLoaded(key)) return key;
    if (Game.resource.existsOrQueued(key)) {
        try {
            await Game.resource.wait(key);
            return key;
        } catch (error) {
            // Error doesn't matter here, so just move on to return the fallback
        }
    }
    if (Game.resource.existsAndLoaded(fallback)) return fallback;
    if (Game.resource.existsOrQueued(fallback)) {
        try {
            await Game.resource.wait(fallback);
        } catch (error) {
            // Once again - can't do anything about it
        }
    }
    // It failed but there's nothing else to return
    return fallback;
}

export default class PayloadHelper {
    public static async getSendCoinsBase64() {
        const key = 'friend_send_life';
        const path = 'resource/graphics/messages/send_coins.png';
        await loadIfNeeded(key, path);

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const titleText = new PIXI.Text(Game.locale.getText('FriendsText1'), Game.style.getStyle('bot_message.title'));
        titleText.pivot.set(0.5, 0.5);
        titleText.position.set(imageSprite.width / 2 - titleText.width / 2, 5);

        imageSprite.addChild(titleText);

        return PIXIRenderer.spriteToBase64(imageSprite);
    }

    public static getScreenshotBase64() {
        const renderTexture = this.getScreenshot();
        PIXIRenderer.renderer.render(PIXIRenderer.sceneContainer, renderTexture);

        return PIXIRenderer.displayObjectToBase64(renderTexture);
    }

    public static getScreenshot() {
        const renderTexture = PIXI.RenderTexture.create(
            PIXIRenderer.renderer.width,
            PIXIRenderer.renderer.height,
            undefined,
            PIXIRenderer.renderer.resolution
        );
        PIXIRenderer.renderer.render(PIXIRenderer.sceneContainer, renderTexture);

        return renderTexture;
    }

    public static async getRequestCoinsBase64() {
        const key = 'friend_request_coins';
        const path = 'resource/graphics/messages/request_coins.png';
        await loadIfNeeded(key, path);

        const base = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const avatarSprite = new PIXI.Sprite(PIXIRenderer.getTexture(Game.player.getAvatarTexture()));

        const titleText = new PIXI.Text(Game.locale.getText('OhNo'), Game.style.getStyle('bot_message.title'));

        base.addChild(avatarSprite);
        base.addChild(imageSprite);
        base.addChild(titleText);

        avatarSprite.pivot.set(0, 0);
        avatarSprite.position.set(384, 101);
        avatarSprite.width = 156;
        avatarSprite.height = 156;

        titleText.pivot.set(0.5, 0.5);
        titleText.position.set(imageSprite.width / 2 - titleText.width / 2, 5);

        return PIXIRenderer.spriteToBase64(base);
    }

    public static async getFriendGiftBase64() {
        const key = 'friend_gift_coins';
        const path = 'resource/graphics/messages/collect_coins.png';
        await loadIfNeeded(key, path);

        const base = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const avatarSprite = new PIXI.Sprite(PIXIRenderer.getTexture(Game.player.getAvatarTexture()));

        base.addChild(avatarSprite);
        base.addChild(imageSprite);

        avatarSprite.pivot.set(0, 0);
        avatarSprite.position.set(244, 73);
        avatarSprite.width = 199;
        avatarSprite.height = 199;

        return PIXIRenderer.spriteToBase64(base);
    }

    public static async getHelpFriendBase64() {
        const key = 'friend_help_card';
        const path = isIOS()
            ? 'resource/graphics/messages/invite_friends.png'
            : 'resource/graphics/messages/friend_help.png';

        await loadIfNeeded(key, path);

        const base = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const avatarSprite = new PIXI.Sprite(PIXIRenderer.getTexture(Game.player.getAvatarTexture()));
        const titleText = new PIXI.Text(
            Game.locale.getText('HelpFriendTitle'),
            Game.style.getStyle('bot_message.title')
        );
        PayloadHelper.setPixiTextBound(titleText, new Point(480, 200));
        if (isIOS()) {
            base.addChild(avatarSprite);
            base.addChild(imageSprite);
        }
        base.addChild(titleText);

        avatarSprite.pivot.set(0, 0);
        avatarSprite.position.set(264, 110);
        avatarSprite.width = 140;
        avatarSprite.height = 140;

        titleText.pivot.set(0.5, 0.5);
        titleText.position.set(imageSprite.width / 2 - titleText.width / 2, 5);

        return PIXIRenderer.spriteToBase64(base);
    }

    public static async getInviteFriendBase64() {
        const key = 'share_invite_letsplay';
        const path = 'resource/graphics/messages/invite_friends.png';
        await loadIfNeeded(key, path);

        const base = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const avatarSprite = new PIXI.Sprite(PIXIRenderer.getTexture(Game.player.getAvatarTexture()));

        const titleText = new PIXI.Text(
            Game.locale.getText('LetsPlayTogether'),
            Game.style.getStyle('bot_message.title')
        );

        titleText.pivot.set(0.5, 0.5);
        titleText.position.set(imageSprite.width / 2 - titleText.width / 2, 5);

        base.addChild(avatarSprite);
        base.addChild(imageSprite);
        base.addChild(titleText);

        avatarSprite.pivot.set(0, 0);
        avatarSprite.position.set(264, 110);
        avatarSprite.width = 140;
        avatarSprite.height = 140;

        return PIXIRenderer.spriteToBase64(base);
    }

    private static setPixiTextBound(textObject: PIXI.Text, value: Point): PIXI.Text {
        // Validate.
        if (!value || value.x <= 0 || value.y <= 0) return;
        // Set text to original font size
        let fontSize: number = Number(textObject.style.fontSize);

        // Reduce size until get a fitable size is found.
        for (fontSize > 0; fontSize--; ) {
            const metrics = PIXI.TextMetrics.measureText(textObject.text, textObject.style, textObject.style.wordWrap);
            if (metrics.width > value.x || metrics.height > value.y) {
                textObject.style.fontSize = fontSize;
            } else {
                break;
            }
        }
        return textObject;
    }

    public static async getBeatHighscoreLevelBase64(
        levelNumber: number,
        playerEntry: IScoreEntry,
        friendEntry: IScoreEntry
    ) {
        const key = 'beat_highscore_level';
        const path = 'resource/graphics/messages/bot_message_beat_highscore_level.png';
        await loadIfNeeded(key, path);

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        // Setup player (winner) info.
        const winnerKey = `player_avatar_${playerEntry.user.playerId}`;
        const winnerAvatar = new PIXI.Sprite(PIXIRenderer.getTexture(await waitForTextureKey(winnerKey, 'no_avatar')));
        winnerAvatar.width = 100;
        winnerAvatar.height = 100;
        winnerAvatar.anchor.set(0.5, 0.5);
        winnerAvatar.x = imageSprite.width * 0.18;
        winnerAvatar.y = imageSprite.height * 0.33;
        imageSprite.addChild(winnerAvatar);

        const winnerNameText = new PIXI.Text(playerEntry.user.handle, {
            fontFamily: 'RussoOne',
            fontSize: 38,
            fill: '#512415',
            align: 'left',
        });
        winnerNameText.anchor.set(0, 0.5);
        winnerNameText.x = imageSprite.width * 0.35;
        winnerNameText.y = imageSprite.height * 0.25;
        imageSprite.addChild(winnerNameText);

        const winnerScoreText = new PIXI.Text(playerEntry.score.toString(), {
            fontFamily: 'RussoOne',
            fontSize: 46,
            fill: '#FFF5D4',
            align: 'left',
        });
        winnerScoreText.anchor.set(0, 0.5);
        winnerScoreText.x = imageSprite.width * 0.35;
        winnerScoreText.y = imageSprite.height * 0.4;
        imageSprite.addChild(winnerScoreText);

        // Setup friend (loser) info.
        const loserKey = `player_avatar_${friendEntry.user.playerId}`;
        const loserAvatar = new PIXI.Sprite(PIXIRenderer.getTexture(await waitForTextureKey(loserKey, 'no_avatar')));
        loserAvatar.width = 80;
        loserAvatar.height = 80;
        loserAvatar.anchor.set(0.5, 0.5);
        loserAvatar.x = imageSprite.width * 0.19;
        loserAvatar.y = imageSprite.height * 0.71;
        imageSprite.addChild(loserAvatar);

        const loserNameText = new PIXI.Text(friendEntry.user.handle, {
            fontFamily: 'RussoOne',
            fontSize: 34,
            fill: '#512415',
            align: 'center',
        });
        loserNameText.anchor.set(0, 0.5);
        loserNameText.x = imageSprite.width * 0.35;
        loserNameText.y = imageSprite.height * 0.65;
        imageSprite.addChild(loserNameText);

        const loserScore = new PIXI.Text(friendEntry.score.toString(), {
            fontFamily: 'RussoOne',
            fontSize: 38,
            fill: '#FFF5D4',
            align: 'center',
        });
        loserScore.anchor.set(0, 0.5);
        loserScore.x = imageSprite.width * 0.35;
        loserScore.y = imageSprite.height * 0.78;
        imageSprite.addChild(loserScore);

        const localeLevelText = Game.locale.getText('Level').replace('[x]', '');
        const levelText = new PIXI.Text(localeLevelText.trim(), {
            fontFamily: 'RussoOne',
            fontSize: 40,
            fill: '#FFF5D4',
            align: 'center',
        });
        levelText.anchor.set(0.5, 0.5);
        levelText.x = imageSprite.width * 0.85;
        levelText.y = imageSprite.height * 0.3;
        imageSprite.addChild(levelText);

        const levelNumberText = new PIXI.Text(levelNumber.toString(), {
            fontFamily: 'RussoOne',
            fontSize: 80,
            fill: '#FFF5D4',
            align: 'center',
        });
        levelNumberText.anchor.set(0.5, 0.5);
        levelNumberText.x = imageSprite.width * 0.85;
        levelNumberText.y = imageSprite.height * 0.5;
        imageSprite.addChild(levelNumberText);

        return PIXIRenderer.spriteToBase64(imageSprite);
    }

    public static async getCropUnlockBase64(fieldId: string) {
        const { key, path } = getCropUnlockShare(fieldId);
        Game.resource.addIfNeeded(key, path);
        await Game.resource.load();

        const layout = new CropUnlockLayout();;;
        layout.base.getComponent(SpriteComponent).texture = key;
        layout.bg.getComponent(SpriteComponent).texture = key;
        layout.avatar.getComponent(SpriteComponent).texture = Game.player.getAvatarTexture();
        layout.quota.getComponent(TextComponent).text = Game.locale.getText('NewCropUnlocked')
            .replace('[x]', Game.locale.getText(fieldId));
        fit(layout.avatar, 152, 152);

        const base64 = PIXIRenderer.spriteToBase64(layout.base.container);
        layout.root.destroy();

        return base64;
    }

    public static async getPetUnlockBase64(petId: string) {
        const { key, path } = getPetUnlockShare(petId);
        Game.resource.addIfNeeded(key, path);
        await Game.resource.load();

        const baseSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const quoteText = GameObject.Instantiate(Element, {
            name: 'field_quote_txt',
            components: [
                {
                    type: 'text',
                    data: {
                        style: Game.style.getStyle('PetUnlockedShare'),
                        bounds: [550, 100],
                        textLocalizationKey: `${petId}SharePetUnlock`,
                    },
                },
            ],
        });

        baseSprite.addChild(imageSprite);
        baseSprite.addChild(quoteText.container);

        quoteText.container.pivot.set(0.5, 0.3);
        quoteText.container.position.set(400, 585);

        return PIXIRenderer.spriteToBase64(baseSprite);
    }

    public static async getFieldUnlockBase64(fieldId: string) {
        const { key, path } = getFieldUnlockShare(fieldId);
        await loadIfNeeded(key, path);

        const baseSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        baseSprite.width = imageSprite.width;
        baseSprite.height = imageSprite.height;

        const avatarSprite = new PIXI.Sprite(PIXIRenderer.getTexture(Game.player.getAvatarTexture()));

        const titleText = new PIXI.Text(Game.locale.getText('NewField'), Game.style.getStyle('popup.new_field'));

        const quoteText = GameObject.Instantiate(Element, {
            name: 'field_quote_txt',
            components: [
                {
                    type: 'text',
                    data: {
                        style: Game.style.getStyle('popup.field_quote'),
                        bounds: [700, 300],
                        textLocalizationKey: `${fieldId}FieldQuote`,
                    },
                },
            ],
        });

        baseSprite.addChild(avatarSprite);
        baseSprite.addChild(imageSprite);
        baseSprite.addChild(titleText);
        baseSprite.addChild(quoteText.container);

        avatarSprite.pivot.set(0, 0);
        avatarSprite.position.set(...perCrop[fieldId].fieldUnlock);
        avatarSprite.width = 135;
        avatarSprite.height = 135;

        titleText.pivot.set(0.5, 0.5);
        titleText.rotation = -25 * DEG_TO_RAD;
        titleText.position.set(70, 110);

        quoteText.container.pivot.set(0.5, 0.3);
        quoteText.container.rotation = -5 * DEG_TO_RAD;
        quoteText.container.position.set(450, 150);

        return PIXIRenderer.spriteToBase64(baseSprite);
    }
    public static async get3StarShareBase64(levelNumber: number) {
        const levelData: ILevelDataFile = Game.resource.get(`levelData_${levelNumber}`);
        const episodeIndex = getEpisodeIndex(levelData.settings);

        const { key, path } = get3StarShare(episodeIndex);
        await loadIfNeeded(key, path);

        const baseSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const avatarSprite = new PIXI.Sprite(PIXIRenderer.getTexture(Game.player.getAvatarTexture()));

        const titleText = new PIXI.Text(
            Game.locale.getText('Level').replace('[x]', levelNumber.toString()),
            Game.style.getStyle('popup.new_field')
        );

        baseSprite.addChild(avatarSprite);
        baseSprite.addChild(imageSprite);
        baseSprite.addChild(titleText);

        avatarSprite.pivot.set(0, 0);

        const position = threeStarPerEpisode[episodeIndex]?.position;
        if (position) avatarSprite.position.set(...position);

        avatarSprite.width = 150;
        avatarSprite.height = 150;

        titleText.pivot.set(0.5, 0.5);
        titleText.rotation = -25 * DEG_TO_RAD;
        titleText.position.set(70, 110);

        return PIXIRenderer.spriteToBase64(baseSprite);
    }
    public static async getHardLevelShareBase64() {
        const { key, path } = getHardLevelShare();
        await loadIfNeeded(key, path);

        const baseSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const text = new PIXI.Text(
            Game.locale.getText('ShareHardLevel'),
            Game.style.getStyle('HardLevelShare')
        );

        baseSprite.addChild(imageSprite);
        baseSprite.addChild(text);

        text.anchor.set(0.5, 0.5);
        text.position.set(833, 400);

        return PIXIRenderer.spriteToBase64(baseSprite);
    }
    public static async getPetMiniGameJackpotBase64() {
        const { key, path } = getMyPetMiniGameJackpotShare();
        await loadIfNeeded(key, path);

        const baseSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const titleText = new PIXI.Text(Game.locale.getText('My_Pet_Mini_Game_Jackpot_Share'), {
            fontFamily: 'FrancoisOne',
            fontSize: '60px',
            fill: '#8d3321',
            align: 'center',
        });

        baseSprite.addChild(titleText);

        titleText.anchor.set(0.5, 0.5);
        titleText.position.set(baseSprite.width / 2 + 45, 608);
        titleText.scale.set(Math.min(740 / titleText.width, 1));

        return PIXIRenderer.spriteToBase64(baseSprite);
    }

    public static async getLuckyWheelBase64(data: ILuckyWheelShareData) {
        const { key, path } = getLuckyWheelShare(data.type);
        await loadIfNeeded(key, path);

        const baseSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        baseSprite.width = imageSprite.width;
        baseSprite.height = imageSprite.height;

        const quoteText = GameObject.Instantiate(Element, {
            name: 'quote_txt',
            components: [
                {
                    type: 'text',
                    data: {
                        style: Game.style.getStyle(`luckyWheel.${data.type}.quote`),
                        textLocalizationKey: `ShareLuckyWheelReward_${data.type}`,
                        align: TextAlignment.CENTER,
                        wordWrap: true,
                        wordWrapWidth: 670
                    },
                },
            ],
        });

        baseSprite.addChild(imageSprite);
        baseSprite.addChild(quoteText.container);

        data.prizes.forEach((prize: { type: RewardType; amount: number; props: { asset: string; wheelAsset?: string; width?: number; scale?: number; } }, index) => {
            const { asset, scale } = prize.props;

            const x = prize.type === RewardType.COINS ? 710
            : data.prizes.length > 1 ? 650 + (index * 300) : 750

            const rewardImage = new PIXI.Sprite(PIXIRenderer.getTexture(asset));

            baseSprite.addChild(rewardImage);

            rewardImage.scale.set(scale ?? 1);
            rewardImage.pivot.set(0.5, 0.5);
            rewardImage.position.set(x, 350);

            const rewardText = new PIXI.Text(
                String(prize.amount),
                Game.style.getStyle(`luckyWheel.${data.type}.reward`)
            );
            baseSprite.addChild(rewardText);

            rewardText.pivot.set(0.5, 0.5);
            rewardText.position.set(x + rewardImage.width, 350);
        });

        quoteText.container.pivot.set(0.5, 0.5);
        quoteText.container.position.set(510, 545);

        return PIXIRenderer.spriteToBase64(baseSprite);
    }
    public static getFarmShareBase64() {
        const shareFrame = new PIXI.Sprite(PIXIRenderer.getTexture('myfarm_shareframe'));

        const farmImage = new PIXI.Sprite(PIXIRenderer.getTexture('farmShareTexture'));
        farmImage.height = 494;
        farmImage.width = 878;
        farmImage.position.set(52.5,46.9);

        shareFrame.addChild(farmImage);

        return PIXIRenderer.spriteToBase64(shareFrame);
    }
}
